<template>
    <DCard
        :title="__('products.partner_api_products.title')"
        padding
    >
        <DAlert
            color="info"
            class="d-flex align-items-center"
        >
            <DIcon
                name="information-outline"
                class="infoIcon"
                :size="20"
            />
            {{ __('products.partner_api_products.info_banner') }}
        </DAlert>

        <p class="mb-3">
            <strong class="partnersCountText">{{ __('products.partner_api_products.partners_count', {count: partnerApiContracts.total}) }}</strong>
        </p>

        <DTable
            :items="partnerApiContracts.data ?? []"
            :fields="[
                {key: 'id', label: __('products.partner_api_products.contract_number')},
                {key: 'customer_name', label: __('products.partner_api_products.customer'), sorter: false, filter: false},
                {key: 'tenant', label: __('products.partner_api_products.tenant'), sorter: false, filter: false},
                {key: 'products', label: __('products.partner_api_products.products'), sorter: false, filter: false},
                {key: 'products_missing_price', label: __('products.partner_api_products.products_missing_price'), sorter: false, filter: false},
                {key: 'actions', label: __('products.partner_api_products.actions'), sorter: false, filter: false},
            ]"
            hover
            sorter
        >
            <template #id="{item}">
                <td>
                    <Link
                        v-if="userPermissions.includes('contract.view')"
                        :href="route('customers.customers.contracts.show', {customer: item.customer_id, contract: item.id})"
                    >
                        #{{ item.id }}
                    </Link>
                    <span v-else>#{{ item.id }}</span>
                </td>
            </template>

            <template #customer_name="{item}">
                <td>
                    <Link
                        v-if="userPermissions.includes('customer.view')"
                        :href="route('customers.customers.show', {customer: item.customer_id})"
                    >
                        {{ item.customer_name }}
                    </Link>
                    <span v-else>{{ item.customer_name }}</span>
                </td>
            </template>

            <template #products_missing_price="{item}">
                <td>
                    <template v-if="item.products === 0">
                        -
                    </template>
                    <span
                        v-else
                        :class="item.products_missing_price > 0 ? 'text-danger' : 'text-success'"
                    >
                        {{ item.products_missing_price }}
                    </span>
                </td>
            </template>

            <template #actions="{item}">
                <td>
                    <div>
                        <DButton
                            :id="`edit_${item.id}`"
                            mini
                            icon="pencil"
                            :disabled="!item.tenant"
                            @click="editPartnerApiProduct(item.id)"
                        />
                    </div>
                </td>
            </template>
        </DTable>

        <DPagination
            v-if="partnerApiContracts.last_page >= 1"
            :active-page="partnerApiContracts.current_page"
            :pages="partnerApiContracts.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>

    <DFabs>
        <DFab
            id="btn-sync"
            icon="sync"
            :label="__('products.partner_api_products.sync_button')"
            :disabled="syncing"
            :class="{ 'syncing-disabled': syncing }"
            @click="syncProducts"
        />
    </DFabs>
</template>

<script setup>
import { Link, router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { __ } from '~@/Composables/translation'

defineProps({
    partnerApiContracts: {
        type: Object,
        default: () => { return {} }
    }
})

const userPermissions = computed(() => usePage().props.auth.permissions)
const syncing = ref(false)

onMounted(() => {
    window.laravelEcho.channel('onecrm.partner_api_products').listen('.partner_api_products.synced', () => {
        syncing.value = false
        router.reload()
    })
})

onUnmounted(() => {
    window.laravelEcho.leave('onecrm.partner_api_products')
})

function syncProducts () {
    syncing.value = true
    router.post(route('products.partner-api-products.sync'))
}

function changePage (page) {
    const queryParameters = {}

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('products.partner-api-products.index'), queryParameters)
}

function editPartnerApiProduct (contractId) {
    router.get(route('products.partner-api-products.edit', { contract: contractId }))
}
</script>

<style lang="scss" scoped>
.partnersCountText {
    padding-left: 10px;
}

.infoIcon {
    padding-right: 5px;
    margin-left: -5px;
}

.syncing-disabled {
    cursor: not-allowed !important;
    pointer-events: auto !important;
}
</style>
